import { useState, useEffect, useRef } from 'react'
import { Link } from "react-router-dom"
import Logo from './logo'
import Dropdown from '../utils/dropdown'
import client from '../../client/Client'
import PreviousSSD from '../../sanityInterfaces/PreviousSSD'

interface IeeeContent {
  image: string,
  link: string
}

export default function Header() {
  const [YearVersion, setYearVersion] = useState("")
  const [allPrevious, setAllPrevious] = useState<Array<{ name: string, link: string }>>([])
  const [IeeeContent, setIeeeContent] = useState<IeeeContent>({
    image: "",
    link: ""
  })
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)

  useEffect(() => {
    client.fetch('*[_type == "YearVersion"]')
      .then((result) => {
        if (result.length > 0) {
          setYearVersion(result[0].YearVersion)
        }
      })
      .catch((error) => {
        console.error('Error fetching data:', error)
      })

    client.fetch('*[_type == "PreviousSSD"] | order(SSDVersionYear desc)')
      .then((result) => {
        if (result.length > 0) {
          const finalArray: Array<{ name: string, link: string }> = result
            .filter((element: PreviousSSD) => element?.SSDVersionYear)
            .map((element: PreviousSSD) => ({
              name: element.SSDVersionYear,
              link: "/learnmore/".concat(element.SSDVersionYear)
            }))

          setAllPrevious(finalArray)
        }
      })
      .catch((error) => {
        console.error('Error fetching data:', error)
      })

    client.fetch('*[_type == "WelcomeParagh"]')
      .then((resul) => {
        if (resul.length > 0 && resul[0]?.IEEE?.asset?._ref && resul[0]?.IEEEWebsite) {
          const myObj = {
            image: resul[0]?.IEEE?.asset?._ref,
            link: resul[0]?.IEEEWebsite
          }
          setIeeeContent(myObj)
        }
      })
      .catch((error) => {
        console.error('Error fetching data:', error)
      })
  }, [])

  const [top, setTop] = useState<boolean>(true)

  const scrollHandler = () => {
    window.pageYOffset > 10 ? setTop(false) : setTop(true)
  }

  useEffect(() => {
    scrollHandler()
    window.addEventListener('scroll', scrollHandler)
    return () => window.removeEventListener('scroll', scrollHandler)
  }, [top])

  const scrollPositionRef = useRef<number>(0)

  const toggleMobileMenu = () => {
    const newState = !isMobileMenuOpen
    setIsMobileMenuOpen(newState)
    setActiveDropdown(null)

    if (newState) {
      scrollPositionRef.current = window.pageYOffset
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
      window.scrollTo(0, scrollPositionRef.current)
    }
  }

  const toggleDropdown = (title: string) => {
    setActiveDropdown(activeDropdown === title ? null : title)
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
    setActiveDropdown(null)
    document.body.style.overflow = ''
    window.scrollTo(0, scrollPositionRef.current)
  }

  return (
    <>
      <header className={`fixed top-0 left-0 w-full z-50 md:bg-opacity-90 transition duration-300 ease-in-out ${!top ? 'bg-white backdrop-blur-sm' : ''}`}>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6">
          <div className="flex items-center justify-between h-16 md:h-20">

            <div className="md:hidden flex items-center z-50">
              <button
                onClick={toggleMobileMenu}
                className="text-gray-600 hover:text-gray-900 focus:outline-none focus:text-gray-900"
                aria-label="Toggle menu"
              >
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>

            <div className="shrink-0 flex space-x-16 items-center mr-16 z-50">
              <Logo />
              {IeeeContent?.image !== "" && IeeeContent?.link !== "" && (
                <a href={IeeeContent?.link} className="block" aria-label="IEEE">
                  <img
                    src={`https://cdn.sanity.io/images/79vgbaej/production/${IeeeContent?.image.slice(6, -4) + '.' + IeeeContent?.image.slice(-3)}`}
                    width={80}
                    height={80}
                    alt="IEEE Logo"
                  />
                </a>
              )}
            </div>

            <nav className="hidden md:flex md:grow overflow-visible">
              <ul className="flex grow justify-end items-center">
                <li>
                  <Dropdown
                    title={'Conferences'}
                    elements={[
                      { name: "SAC", link: "/sac" },
                      { name: "PSE", link: "/pse" },
                      { name: "SCI", link: "/sci" },
                      { name: "CSP", link: "/csp" }
                    ]}
                  />
                </li>

                <li>
                  <Dropdown
                    title={'Committees'}
                    elements={[
                      { name: "Chairs", link: "/chairs" },
                      { name: "Steering Committee", link: "/steering-committee" },
                      { name: "Scientific Committee", link: "/scientific-committee" },
                      { name: "Organizing Committee", link: "/organizing-committee" },
                      { name: "Ambassadors", link: "/ambassadors" },
                      { name: "Sponsors", link: "/sponsors" },
                      { name: "Organizers", link: "/organizers" }
                    ]}
                  />
                </li>

                <li>
                  <a href="/submissions" className="font-medium text-gray-600 hover:text-gray-900 px-5 py-3 flex items-center transition duration-150 ease-in-out">
                    Submission
                  </a>
                </li>

                <li>
                  <Dropdown
                    title={'Programs'}
                    elements={[
                      { name: "Technical Program ", link: `/technical-program/${YearVersion}` },
                      { name: "Speakers ", link: "/speakers" },
                      { name: "Special Sessions", link: "/special-sessions" },
                      { name: "Call for Special Sessions", link: "/call-for-special-sessions" },
                      { name: "PhD Student Contest", link: "/phd-student-contest" },
                      { name: "Workshop and Tutorial Sessions", link: "/workshop-and-tutorial-sessions" },
                      { name: "Workshops ", link: "/workshops" },
                      { name: "Social Program", link: "/social-program" },
                      { name: "Awards", link: "/awards" }
                    ]}
                  />
                </li>

                <li>
                  <Dropdown
                    title={'Registrations'}
                    elements={[
                      { name: "Registration", link: "/registration" },
                      { name: "Travel Information", link: "/travel-information" },
                      { name: "Accommodation", link: "/accomodation" },
                      { name: "Hotel Information", link: "/hotel-inforamtion" }
                    ]}
                  />
                </li>

                <li>
                  <Dropdown
                    title={'SSD'}
                    elements={[
                      {
                        name: "SSD in IEEE Xplore",
                        link: "https://ieeexplore.ieee.org/xpl/conhome/1002453/all-proceedings"
                      },
                      {
                        name: "Top cited papers",
                        link: "/top-cited-papers"
                      },
                      ...allPrevious
                    ]}
                  />
                </li>

                <li>
                  <Link
                    to="/contactus"
                    className="btn-sm p-2 px-6 rounded-md text-white bg-red-800 hover:bg-red-500 ml-3 grid items-center justify-center"
                  >
                    <span className='text-sm'>Contact Us</span>
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </header>

      <div className={`md:hidden fixed inset-0 z-40 transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="relative bg-white w-full h-full overflow-y-auto mt-8">
          <nav className="p-6 pt-12">
            <ul className="space-y-4">

              <li>
                <button
                  onClick={() => toggleDropdown('Conferences')}
                  className="flex items-center justify-between w-full py-4 text-left text-gray-600 hover:text-gray-900 font-medium border-b border-gray-200"
                >
                  <span>Conferences</span>
                  <svg
                    className={`h-5 w-5 transition-transform duration-200 ${activeDropdown === 'Conferences' ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <div className={`overflow-hidden transition-all duration-200 ${activeDropdown === 'Conferences' ? 'max-h-96' : 'max-h-0'}`}>
                  <ul className="pl-4 pb-2 space-y-2">
                    <li><a href="/sac" onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out border-b border-gray-100">SAC</a></li>
                    <li><a href="/pse" onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out border-b border-gray-100">PSE</a></li>
                    <li><a href="/sci" onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out border-b border-gray-100">SCI</a></li>
                    <li><a href="/csp" onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out">CSP</a></li>
                  </ul>
                </div>
              </li>

              <li>
                <button
                  onClick={() => toggleDropdown('Committees')}
                  className="flex items-center justify-between w-full py-4 text-left text-gray-600 hover:text-gray-900 font-medium border-b border-gray-200"
                >
                  <span>Committees</span>
                  <svg
                    className={`h-5 w-5 transition-transform duration-200 ${activeDropdown === 'Committees' ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <div className={`overflow-hidden transition-all duration-200 ${activeDropdown === 'Committees' ? 'max-h-96' : 'max-h-0'}`}>
                  <ul className="pl-4 pb-2 space-y-2">
                    <li><a href="/chairs" onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out border-b border-gray-100">Chairs</a></li>
                    <li><a href="/steering-committee" onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out border-b border-gray-100">Steering Committee</a></li>
                    <li><a href="/scientific-committee" onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out border-b border-gray-100">Scientific Committee</a></li>
                    <li><a href="/organizing-committee" onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out border-b border-gray-100">Organizing Committee</a></li>
                    <li><a href="/ambassadors" onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out border-b border-gray-100">Ambassadors</a></li>
                    <li><a href="/sponsors" onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out border-b border-gray-100">Sponsors</a></li>
                    <li><a href="/organizers" onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out">Organizers</a></li>
                  </ul>
                </div>
              </li>

              <li>
                <a
                  href="/submissions"
                  onClick={closeMobileMenu}
                  className="block py-4 text-gray-600 hover:text-gray-900 font-medium transition duration-150 ease-in-out border-b border-gray-200"
                >
                  Submission
                </a>
              </li>

              <li>
                <button
                  onClick={() => toggleDropdown('Programs')}
                  className="flex items-center justify-between w-full py-4 text-left text-gray-600 hover:text-gray-900 font-medium border-b border-gray-200"
                >
                  <span>Programs</span>
                  <svg
                    className={`h-5 w-5 transition-transform duration-200 ${activeDropdown === 'Programs' ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <div className={`overflow-hidden transition-all duration-200 ${activeDropdown === 'Programs' ? 'max-h-96' : 'max-h-0'}`}>
                  <ul className="pl-4 pb-2 space-y-2">
                    <li><a href={`/technical-program/${YearVersion}`} onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out border-b border-gray-100">Technical Program</a></li>
                    <li><a href="/speakers" onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out border-b border-gray-100">Speakers</a></li>
                    <li><a href="/special-sessions" onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out border-b border-gray-100">Special Sessions</a></li>
                    <li><a href="/call-for-special-sessions" onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out border-b border-gray-100">Call for Special Sessions</a></li>
                    <li><a href="/phd-student-contest" onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out border-b border-gray-100">PhD Student Contest</a></li>
                    <li><a href="/workshop-and-tutorial-sessions" onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out border-b border-gray-100">Workshop and Tutorial Sessions</a></li>
                    <li><a href="/workshops" onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out border-b border-gray-100">Workshops</a></li>
                    <li><a href="/social-program" onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out border-b border-gray-100">Social Program</a></li>
                    <li><a href="/awards" onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out">Awards</a></li>
                  </ul>
                </div>
              </li>

              <li>
                <button
                  onClick={() => toggleDropdown('Registrations')}
                  className="flex items-center justify-between w-full py-4 text-left text-gray-600 hover:text-gray-900 font-medium border-b border-gray-200"
                >
                  <span>Registrations</span>
                  <svg
                    className={`h-5 w-5 transition-transform duration-200 ${activeDropdown === 'Registrations' ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <div className={`overflow-hidden transition-all duration-200 ${activeDropdown === 'Registrations' ? 'max-h-96' : 'max-h-0'}`}>
                  <ul className="pl-4 pb-2 space-y-2">
                    <li><a href="/registration" onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out border-b border-gray-100">Registration</a></li>
                    <li><a href="/travel-information" onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out border-b border-gray-100">Travel Information</a></li>
                    <li><a href="/accomodation" onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out border-b border-gray-100">Accommodation</a></li>
                    <li><a href="/hotel-inforamtion" onClick={closeMobileMenu} className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out">Hotel Information</a></li>
                  </ul>
                </div>
              </li>

              <li>
                <button
                  onClick={() => toggleDropdown('SSD')}
                  className="flex items-center justify-between w-full py-4 text-left text-gray-600 hover:text-gray-900 font-medium border-b border-gray-200"
                >
                  <span>SSD</span>
                  <svg
                    className={`h-5 w-5 transition-transform duration-200 ${activeDropdown === 'SSD' ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <div className={`overflow-hidden transition-all duration-200 ${activeDropdown === 'SSD' ? 'max-h-96' : 'max-h-0'}`}>
                  <ul className="pl-4 pb-2 space-y-2">
                    <li>
                      <a
                        href="https://ieeexplore.ieee.org/xpl/conhome/1002453/all-proceedings"
                        onClick={closeMobileMenu}
                        className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out border-b border-gray-100"
                      >
                        SSD in IEEE Xplore
                      </a>
                    </li>

                    <li>
                      <a
                        href="/top-cited-papers"
                        onClick={closeMobileMenu}
                        className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out border-b border-gray-100"
                      >
                        Top cited papers
                      </a>
                    </li>

                    {allPrevious.map((item, index) => (
                      item.name && item.link ? (
                        <li key={index}>
                          <a
                            href={item.link}
                            onClick={closeMobileMenu}
                            className="block py-3 text-gray-500 hover:text-gray-900 transition duration-150 ease-in-out border-b border-gray-100"
                          >
                            {item.name}
                          </a>
                        </li>
                      ) : null
                    ))}
                  </ul>
                </div>
              </li>

              <li className="mt-8">
                <Link
                  to="/contactus"
                  onClick={closeMobileMenu}
                  className="block w-full py-4 px-6 text-center rounded-md text-white bg-red-800 hover:bg-red-500 transition duration-150 ease-in-out font-medium"
                >
                  Contact Us
                </Link>
              </li>

            </ul>
          </nav>
        </div>
      </div>
    </>
  )
}